/**
 * Command palette (⌘K / Ctrl+K). Single keystroke jumps anywhere in Engage:
 *
 *   - Navigate to any of the 6 pages
 *   - Trigger quick actions (new partner, new voucher, theme toggle)
 *   - Search partners by name / email — debounced server-side query
 *
 * Pattern follows Linear / Notion / Raycast: modal dialog at top of viewport,
 * sectioned result list, keyboard-first navigation. The palette is mounted
 * once at App level (so it's reachable from any page) and listens for the
 * global ⌘K shortcut.
 *
 * For "create" actions, we navigate to the target page with `?new=1` and the
 * destination page's effect auto-opens its CreateDrawer. Keeps the palette
 * dumb (no shared mutable open-state) and the page pages still own their UI.
 */

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  createContext,
  useContext,
} from "react";
import {
  Box,
  Dialog,
  Divider,
  InputBase,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import DashboardIcon from "@mui/icons-material/SpaceDashboardOutlined";
import PeopleIcon from "@mui/icons-material/GroupsOutlined";
import VoucherIcon from "@mui/icons-material/LocalOfferOutlined";
import RedeemIcon from "@mui/icons-material/RedeemOutlined";
import PaymentsIcon from "@mui/icons-material/PaymentsOutlined";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import LocalOfferIcon from "@mui/icons-material/LocalOfferOutlined";
import LightModeIcon from "@mui/icons-material/LightModeOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import PersonIcon from "@mui/icons-material/PersonOutline";

import { api } from "../../lib/api";
import { tokens } from "../../lib/theme";
import { useColorMode } from "../providers/ColorModeProvider";
import { useI18n } from "../providers/I18nProvider";

/* ── Public API ──────────────────────────────────────────────────────── */

interface PaletteContextValue {
  open: () => void;
}

const PaletteContext = createContext<PaletteContextValue>({ open: () => {} });

/** Anywhere inside the Shell, call `useCommandPalette().open()` to trigger
 *  the palette programmatically (e.g. from a toolbar button). */
export function useCommandPalette(): PaletteContextValue {
  return useContext(PaletteContext);
}

/* ── Item model ──────────────────────────────────────────────────────── */

interface PaletteItem {
  id: string;
  /** Section label shown above the item group. */
  group: "Navigate" | "Actions" | "Partners";
  label: string;
  hint?: string;
  icon: ReactNode;
  /** Triggered on Enter / click. Closes the palette afterwards. */
  run: () => void;
  /** Used for keyword filtering in addition to label/hint. */
  keywords?: string[];
}

/* ── Provider + dialog ───────────────────────────────────────────────── */

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { toggle: toggleTheme } = useColorMode();
  const { t } = useI18n();

  // Global ⌘K / Ctrl+K listener.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setIsOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);
  const open = useCallback(() => setIsOpen(true), []);

  const staticItems: PaletteItem[] = useMemo(
    () => [
      // Navigate
      { id: "nav-overview",    group: "Navigate", label: t("nav.overview"),    hint: t("shell.section.dashboard"), icon: <DashboardIcon fontSize="small" />, run: () => navigate("/overview") },
      { id: "nav-partners",    group: "Navigate", label: t("nav.partners"),    hint: t("shell.section.program"),   icon: <PeopleIcon    fontSize="small" />, run: () => navigate("/partners") },
      { id: "nav-vouchers",    group: "Navigate", label: t("nav.vouchers"),    hint: t("shell.section.program"),   icon: <VoucherIcon   fontSize="small" />, run: () => navigate("/vouchers") },
      { id: "nav-redemptions", group: "Navigate", label: t("nav.redemptions"), hint: t("shell.section.program"),   icon: <RedeemIcon    fontSize="small" />, run: () => navigate("/redemptions") },
      { id: "nav-rules",       group: "Navigate", label: t("nav.rules"),       hint: t("shell.section.program"),   icon: <RedeemIcon    fontSize="small" />, run: () => navigate("/rules") },
      { id: "nav-payouts",     group: "Navigate", label: t("nav.payouts"),     hint: t("shell.section.finance"),   icon: <PaymentsIcon  fontSize="small" />, run: () => navigate("/payouts") },
      { id: "nav-audit",       group: "Navigate", label: t("nav.audit"),       hint: t("shell.section.insights"),  icon: <HistoryIcon   fontSize="small" />, run: () => navigate("/audit") },

      // Actions
      { id: "act-new-partner",  group: "Actions", label: t("command.newPartner"),  hint: t("command.newPartner.hint"),  icon: <AddCircleOutlineIcon fontSize="small" />, run: () => navigate("/partners?new=1"), keywords: ["create", "add", "invite"] },
      { id: "act-new-voucher",  group: "Actions", label: t("command.newVoucher"),  hint: t("command.newVoucher.hint"),  icon: <LocalOfferIcon       fontSize="small" />, run: () => navigate("/vouchers?new=1"), keywords: ["create", "issue", "code"] },
      { id: "act-generate-payouts", group: "Actions", label: t("command.generatePayouts"), hint: t("command.generatePayouts.hint"),  icon: <PaymentsIcon fontSize="small" />, run: () => navigate("/payouts"), keywords: ["batch", "month", "draft", "sepa"] },
      { id: "act-toggle-theme", group: "Actions", label: t("command.toggleTheme"), hint: t("command.toggleTheme.hint"),    icon: <LightModeIcon        fontSize="small" />, run: () => toggleTheme(), keywords: ["dark", "light", "mode"] },
    ],
    [navigate, toggleTheme, t],
  );

  return (
    <PaletteContext.Provider value={{ open }}>
      {children}
      <PaletteDialog isOpen={isOpen} onClose={close} staticItems={staticItems} />
    </PaletteContext.Provider>
  );
}

/* ── Dialog ──────────────────────────────────────────────────────────── */

function PaletteDialog({
  isOpen,
  onClose,
  staticItems,
}: {
  isOpen: boolean;
  onClose: () => void;
  staticItems: PaletteItem[];
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { t } = useI18n();

  const groupLabel = (group: PaletteItem["group"]) =>
    group === "Navigate"
      ? t("command.group.navigate")
      : group === "Actions"
        ? t("command.group.actions")
        : t("command.group.partners");

  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [partnerHits, setPartnerHits] = useState<PaletteItem[]>([]);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Reset state on each open.
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setActiveIdx(0);
      setPartnerHits([]);
    }
  }, [isOpen]);

  // Debounced server-side partner search.
  useEffect(() => {
    if (!isOpen || !query.trim()) {
      setPartnerHits([]);
      return;
    }
    const handle = window.setTimeout(async () => {
      try {
        const res = await api.get<{ data: { id: string; display_name: string; email: string }[] }>(
          `/engage/partners?q=${encodeURIComponent(query)}&limit=5`,
        );
        setPartnerHits(
          res.data.map((p) => ({
            id: `partner-${p.id}`,
            group: "Partners" as const,
            label: p.display_name,
            hint: p.email,
            icon: <PersonIcon fontSize="small" />,
            run: () => navigate(`/partners/${p.id}`),
          })),
        );
      } catch {
        setPartnerHits([]);
      }
    }, 200);
    return () => window.clearTimeout(handle);
  }, [query, isOpen, navigate]);

  // Filter static items by query (case-insensitive substring on label,
  // hint, and keywords).
  const filteredStatic = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return staticItems;
    return staticItems.filter((it) => {
      const hay = [it.label, it.hint ?? "", ...(it.keywords ?? [])].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [staticItems, query]);

  // Flat list for keyboard navigation (Partners section pinned at the bottom).
  const allItems: PaletteItem[] = useMemo(
    () => [...filteredStatic, ...partnerHits],
    [filteredStatic, partnerHits],
  );

  // Group rendering — preserve order: Navigate, Actions, Partners.
  const grouped = useMemo(() => {
    const out: { group: string; items: PaletteItem[] }[] = [];
    const order: PaletteItem["group"][] = ["Navigate", "Actions", "Partners"];
    for (const g of order) {
      const items = allItems.filter((it) => it.group === g);
      if (items.length) out.push({ group: g, items });
    }
    return out;
  }, [allItems]);

  // Clamp active index when results shrink.
  useEffect(() => {
    if (activeIdx >= allItems.length) setActiveIdx(0);
  }, [allItems.length, activeIdx]);

  const runActive = useCallback(() => {
    const it = allItems[activeIdx];
    if (it) {
      it.run();
      onClose();
    }
  }, [allItems, activeIdx, onClose]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(0, allItems.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      runActive();
    }
  };

  // Scroll active row into view.
  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    node?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        elevation: 0,
        sx: {
          mt: { xs: 4, md: 10 },
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          boxShadow: isDark
            ? `0 24px 80px -16px ${alpha("#000", 0.6)}`
            : `0 24px 80px -16px ${alpha(tokens.brand.purple, 0.25)}`,
          overflow: "hidden",
        },
      }}
      sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}
    >
      {/* Search input */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.5 }}>
        <SearchIcon fontSize="small" sx={{ color: "text.tertiary" }} />
        <InputBase
          autoFocus
          fullWidth
          placeholder={t("command.placeholder")}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIdx(0);
          }}
          onKeyDown={onKeyDown}
          sx={{
            fontSize: 16,
            fontWeight: 450,
            letterSpacing: "-0.01em",
            "& input::placeholder": { color: "text.tertiary", opacity: 1 },
          }}
        />
        <Kbd label="esc" />
      </Box>

      <Divider />

      {/* Results */}
      <Box ref={listRef} sx={{ maxHeight: 420, overflowY: "auto", py: 1 }}>
        {grouped.length === 0 ? (
          <Box sx={{ px: 3, py: 5, textAlign: "center" }}>
            <Typography variant="body2" color="text.tertiary">
              {t("command.noMatches")} <Box component="span" sx={{ fontFamily: tokens.font.mono }}>{`"${query}"`}</Box>
            </Typography>
          </Box>
        ) : (
          grouped.map((section) => (
            <Box key={section.group} sx={{ mb: 0.5 }}>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  px: 2.5,
                  pt: 1,
                  pb: 0.5,
                  fontSize: 10.5,
                  letterSpacing: "0.08em",
                  color: "text.tertiary",
                }}
              >
                {groupLabel(section.group as PaletteItem["group"])}
              </Typography>
              {section.items.map((it) => {
                const idx = allItems.indexOf(it);
                const isActive = idx === activeIdx;
                return (
                  <Box
                    key={it.id}
                    data-idx={idx}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => {
                      it.run();
                      onClose();
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mx: 1,
                      px: 1.5,
                      py: 1,
                      borderRadius: 1.5,
                      cursor: "pointer",
                      bgcolor: isActive ? alpha(tokens.brand.purple, isDark ? 0.18 : 0.08) : "transparent",
                      color: isActive ? "primary.main" : "text.primary",
                      transition: "background-color 80ms ease",
                    }}
                  >
                    <Box sx={{ color: isActive ? "primary.main" : "text.tertiary", display: "flex" }}>
                      {it.icon}
                    </Box>
                    <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.1}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 500,
                          lineHeight: 1.3,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {it.label}
                      </Typography>
                      {it.hint && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: isActive ? alpha(theme.palette.primary.main, 0.75) : "text.tertiary",
                            fontSize: 11.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {it.hint}
                        </Typography>
                      )}
                    </Stack>
                    {isActive && <Kbd label="↵" />}
                  </Box>
                );
              })}
            </Box>
          ))
        )}
      </Box>

      <Divider />

      {/* Footer */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ px: 2.5, py: 1, color: "text.tertiary", fontSize: 11.5 }}
      >
        <FooterHint k="↑↓" label={t("command.footer.navigate")} />
        <FooterHint k="↵"  label={t("command.footer.select")} />
        <FooterHint k="esc" label={t("common.close")} />
      </Stack>
    </Dialog>
  );
}

/* ── Tiny helpers ────────────────────────────────────────────────────── */

function Kbd({ label }: { label: string }) {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 22,
        height: 20,
        px: 0.6,
        borderRadius: 0.75,
        bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.1 : 0.06),
        color: "text.secondary",
        fontFamily: tokens.font.mono,
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: "0.02em",
      }}
    >
      {label}
    </Box>
  );
}

function FooterHint({ k, label }: { k: string; label: string }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Kbd label={k} />
      <Typography variant="caption" sx={{ color: "text.tertiary", fontSize: 11.5 }}>
        {label}
      </Typography>
    </Stack>
  );
}
